import { subDays } from 'date-fns';
import type { CollectionStrategyItem } from '@/types/collection-strategies';

export const mockStrategies: CollectionStrategyItem[] = [
  {
    id: 'STR-001',
    name: 'Standard Collection',
    type: 'Standard',
    description: 'Default collection workflow for regular customers with good payment history',
    status: 'Active',
    activeCasesCount: 45,
    successRate: 78.5,
    avgDaysToPayment: 22,
    trigger: {
      type: 'days_overdue',
      daysOverdue: 7,
    },
    actions: [
      {
        id: 'ACT-001',
        type: 'email',
        timing: {
          type: 'days_from_trigger',
          daysFromTrigger: 0,
        },
        templateName: 'Friendly Reminder',
        details: 'Send initial payment reminder email',
        order: 1,
      },
      {
        id: 'ACT-002',
        type: 'phone_call',
        timing: {
          type: 'days_from_previous',
          daysFromPrevious: 7,
        },
        details: 'Follow-up call to discuss payment',
        order: 2,
      },
      {
        id: 'ACT-003',
        type: 'payment_link',
        timing: {
          type: 'days_from_previous',
          daysFromPrevious: 3,
        },
        details: 'Send secure payment link',
        order: 3,
      },
    ],
    assignmentRules: [
      {
        criterion: 'days_overdue_range',
        enabled: true,
        config: {
          minValue: 7,
          maxValue: 30,
        },
      },
      {
        criterion: 'amount_range',
        enabled: true,
        config: {
          minValue: 1000,
          maxValue: 50000,
        },
      },
    ],
    performanceMetrics: {
      totalCasesProcessed: 342,
      successfulResolutions: 268,
      averageResolutionTime: 22,
      collectionRate: 78.5,
      customerSatisfactionScore: 4.2,
    },
    createdAt: subDays(new Date(), 180),
    updatedAt: subDays(new Date(), 15),
  },
  {
    id: 'STR-002',
    name: 'VIP Collection',
    type: 'VIP',
    description: 'Gentle approach for high-value customers with strong relationships',
    status: 'Active',
    activeCasesCount: 18,
    successRate: 85.2,
    avgDaysToPayment: 15,
    trigger: {
      type: 'days_overdue',
      daysOverdue: 14,
    },
    actions: [
      {
        id: 'ACT-004',
        type: 'phone_call',
        timing: {
          type: 'days_from_trigger',
          daysFromTrigger: 0,
        },
        details: 'Personal call from account manager',
        order: 1,
      },
      {
        id: 'ACT-005',
        type: 'email',
        timing: {
          type: 'days_from_previous',
          daysFromPrevious: 5,
        },
        templateName: 'VIP Courtesy Reminder',
        details: 'Personalized email with payment options',
        order: 2,
      },
    ],
    assignmentRules: [
      {
        criterion: 'customer_segment',
        enabled: true,
        config: {
          selectedValues: ['VIP', 'Enterprise'],
        },
      },
      {
        criterion: 'relationship_type',
        enabled: true,
        config: {
          selectedValues: ['Strategic Partner', 'Key Account'],
        },
      },
    ],
    aiBehavior: {
      communicationTone: 1,
      formalityLevel: 'professional',
      personalizationDepth: 'deep',
      contextFactors: {
        paymentHistory: true,
        riskProfile: true,
        relationshipDuration: true,
        industryContext: true,
        economicFactors: true,
        previousDisputes: false,
        communicationPreferences: false,
      },
      timingOptimization: true,
      escalationTriggers: {
        noResponseDays: 14,
        brokenPromises: 2,
        disputeRaised: true,
      },
      humanReviewRequired: true,
    },
    performanceMetrics: {
      totalCasesProcessed: 156,
      successfulResolutions: 133,
      averageResolutionTime: 15,
      collectionRate: 85.2,
      customerSatisfactionScore: 4.7,
    },
    createdAt: subDays(new Date(), 150),
    updatedAt: subDays(new Date(), 10),
  },
  {
    id: 'STR-003',
    name: 'Intensive Collection',
    type: 'Intensive',
    description: 'Aggressive workflow for high-risk accounts with poor payment history',
    status: 'Active',
    activeCasesCount: 32,
    successRate: 65.8,
    avgDaysToPayment: 35,
    trigger: {
      type: 'days_overdue',
      daysOverdue: 30,
    },
    actions: [
      {
        id: 'ACT-006',
        type: 'phone_call',
        timing: {
          type: 'days_from_trigger',
          daysFromTrigger: 0,
        },
        details: 'Immediate contact attempt',
        order: 1,
      },
      {
        id: 'ACT-007',
        type: 'email',
        timing: {
          type: 'days_from_previous',
          daysFromPrevious: 2,
        },
        templateName: 'Urgent Payment Required',
        details: 'Formal demand letter',
        order: 2,
      },
      {
        id: 'ACT-008',
        type: 'sms',
        timing: {
          type: 'days_from_previous',
          daysFromPrevious: 3,
        },
        details: 'SMS reminder',
        order: 3,
      },
      {
        id: 'ACT-009',
        type: 'escalation',
        timing: {
          type: 'days_from_previous',
          daysFromPrevious: 7,
        },
        details: 'Escalate to collections manager',
        order: 4,
      },
    ],
    assignmentRules: [
      {
        criterion: 'days_overdue_range',
        enabled: true,
        config: {
          minValue: 30,
          maxValue: 90,
        },
      },
      {
        criterion: 'risk_profile',
        enabled: true,
        config: {
          selectedValues: ['High Risk', 'Very High Risk'],
        },
      },
    ],
    aiBehavior: {
      communicationTone: 4,
      formalityLevel: 'formal',
      personalizationDepth: 'minimal',
      contextFactors: {
        paymentHistory: true,
        riskProfile: true,
        relationshipDuration: false,
        industryContext: false,
        economicFactors: false,
        previousDisputes: false,
        communicationPreferences: false,
      },
      timingOptimization: true,
      escalationTriggers: {
        noResponseDays: 7,
        brokenPromises: 1,
        disputeRaised: true,
      },
      humanReviewRequired: false,
    },
    performanceMetrics: {
      totalCasesProcessed: 245,
      successfulResolutions: 161,
      averageResolutionTime: 35,
      collectionRate: 65.8,
      customerSatisfactionScore: 3.1,
    },
    createdAt: subDays(new Date(), 200),
    updatedAt: subDays(new Date(), 20),
  },
  {
    id: 'STR-004',
    name: 'High Risk Collection',
    type: 'High Risk',
    description: 'Maximum effort for severely overdue accounts at risk of write-off',
    status: 'Active',
    activeCasesCount: 12,
    successRate: 45.3,
    avgDaysToPayment: 52,
    trigger: {
      type: 'days_overdue',
      daysOverdue: 90,
    },
    actions: [
      {
        id: 'ACT-010',
        type: 'phone_call',
        timing: {
          type: 'days_from_trigger',
          daysFromTrigger: 0,
        },
        details: 'Immediate escalation call',
        order: 1,
      },
      {
        id: 'ACT-011',
        type: 'email',
        timing: {
          type: 'days_from_previous',
          daysFromPrevious: 1,
        },
        templateName: 'Final Notice',
        details: 'Final demand before legal action',
        order: 2,
      },
      {
        id: 'ACT-012',
        type: 'account_action',
        timing: {
          type: 'days_from_previous',
          daysFromPrevious: 7,
        },
        details: 'Place account on hold',
        order: 3,
      },
      {
        id: 'ACT-013',
        type: 'escalation',
        timing: {
          type: 'days_from_previous',
          daysFromPrevious: 14,
        },
        details: 'Refer to legal department',
        order: 4,
      },
    ],
    assignmentRules: [
      {
        criterion: 'days_overdue_range',
        enabled: true,
        config: {
          minValue: 90,
        },
      },
      {
        criterion: 'amount_range',
        enabled: true,
        config: {
          minValue: 10000,
        },
      },
    ],
    performanceMetrics: {
      totalCasesProcessed: 89,
      successfulResolutions: 40,
      averageResolutionTime: 52,
      collectionRate: 45.3,
      customerSatisfactionScore: 2.5,
    },
    createdAt: subDays(new Date(), 220),
    updatedAt: subDays(new Date(), 30),
  },
  {
    id: 'STR-005',
    name: 'Payment Plan Follow-up',
    type: 'Standard',
    description: 'Monitoring and follow-up for customers on payment plans',
    status: 'Active',
    activeCasesCount: 20,
    successRate: 82.1,
    avgDaysToPayment: 18,
    trigger: {
      type: 'promise_broken',
    },
    actions: [
      {
        id: 'ACT-014',
        type: 'phone_call',
        timing: {
          type: 'days_from_trigger',
          daysFromTrigger: 1,
        },
        details: 'Contact regarding missed payment',
        order: 1,
      },
      {
        id: 'ACT-015',
        type: 'payment_plan',
        timing: {
          type: 'days_from_previous',
          daysFromPrevious: 3,
        },
        details: 'Offer revised payment plan',
        order: 2,
      },
    ],
    assignmentRules: [
      {
        criterion: 'payment_behavior',
        enabled: true,
        config: {
          selectedValues: ['Payment Plan Active', 'Promise to Pay'],
        },
      },
    ],
    performanceMetrics: {
      totalCasesProcessed: 178,
      successfulResolutions: 146,
      averageResolutionTime: 18,
      collectionRate: 82.1,
      customerSatisfactionScore: 4.0,
    },
    createdAt: subDays(new Date(), 120),
    updatedAt: subDays(new Date(), 5),
  },
];
